import { useEffect } from "react";

import { logInfoWithDebugFlag } from "../../utils/logger";

import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { sectionSelectors, selectCurrentSection, selectCurrentUserId } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector, usePromiseEffect } from "store/store-hooks";
import { trackUserProperty } from "utils/amplitude";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { Outlet, useLocation, useNavigate, useParams } from "utils/routing";
import { getDefaultSection } from "utils/section";
import { sectionHomeUrl } from "utils/urls";
import { getWindowId } from "utils/window";

export default function Layout() {
  const currentSection = useAppSelector(selectCurrentSection);
  const sections = useListSelector(sectionSelectors.selectAll);
  const userId = useAppSelector(selectCurrentUserId);
  const currentSectionId = currentSection?.id;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const homeUrl = useHomeUrl();

  useEffect(() => {
    // There might be a case where we briefly don't have a current section
    if (currentSection?.id) {
      const state = store.getState();

      // TODO: We're seeing this message on the course monitoring dashboard.
      logInfoWithDebugFlag("debug_pubsub", `Attempting to set new section version`, {
        section_version: currentSection?.section_version,
        section_id: currentSection?.id,
        windowId: getWindowId(),
        userId: selectCurrentUserId(state),
      });

      dispatch(viewStateActions.setSectionVersionTo(currentSection?.section_version));
    }
  }, [currentSection?.section_version, currentSection?.id, dispatch]);

  // Ensure this component refreshes when we navigate between section routes
  const pathParams = useParams();
  const routeSectionId = pathParams.sectionId;

  const { isLoading, data } = usePromiseEffect(() => {
    if (userId) {
      return storeApi.sections.list({}, { skipToast: true });
    }
    return Promise.resolve(null);
  }, [userId]);

  const isSectionLinkRoute = /\/section\/link\/?$/.test(location.pathname);

  useEffect(() => {
    if (isSectionLinkRoute) {
      return;
    }

    if (!userId || isLoading || !data) {
      return;
    }

    if (sections.length === 0) {
      navigate(homeUrl);
      return;
    }

    if (!routeSectionId || !sections.some((s) => s.id === routeSectionId)) {
      const newRouteSectionId: string = currentSectionId || getDefaultSection(sections).id;

      navigate(sectionHomeUrl(newRouteSectionId));
      return;
    }

    if (routeSectionId && currentSectionId !== routeSectionId) {
      dispatch(viewStateActions.setCurrentSectionId(routeSectionId));
      trackUserProperty("courseId", routeSectionId);
    }
  }, [
    routeSectionId,
    currentSectionId,
    isSectionLinkRoute,
    location,
    dispatch,
    navigate,
    homeUrl,
    isLoading,
    data,
    sections,
    userId,
  ]);

  return <Outlet />;
}
